// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { MultiTimezoneTooltip } from '@/components/multi-timezone-tooltip';
import { InventoryLot } from '@/inventory/api/types';
import { useItemLots } from '@/inventory/hooks';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  Collapse,
  FormControlLabel,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useState } from 'react';

interface InventoryLotDetailsProps {
  itemKey: string;
  locationKey: string;
  expanded: boolean;
}

const HEADER_CELL_SX = {
  fontWeight: 600,
  fontSize: '11px',
  textTransform: 'uppercase',
} as const;

function LotTableHeader() {
  return (
    <TableHead>
      <TableRow>
        <TableCell sx={HEADER_CELL_SX}>Lot ID</TableCell>
        <TableCell align="right" sx={HEADER_CELL_SX}>
          Quantity
        </TableCell>
        <TableCell sx={HEADER_CELL_SX}>Received</TableCell>
        <TableCell sx={HEADER_CELL_SX}>Batch ID</TableCell>
        <TableCell sx={HEADER_CELL_SX}>Batch reference</TableCell>
        <TableCell align="right" sx={HEADER_CELL_SX}>
          Batch total
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

interface LotRowProps {
  lot: InventoryLot;
  isZeroQuantity: boolean;
}

const ID_SHORT_LENGTH = 8;

function LotRow({ lot, isZeroQuantity }: LotRowProps) {
  const textColor = isZeroQuantity ? 'text.disabled' : 'text.primary';
  const lotIdShort = lot.id.slice(0, ID_SHORT_LENGTH);
  const batchIdShort = lot.batch.id.slice(0, ID_SHORT_LENGTH);
  const receivedAtDateTime = DateTime.fromJSDate(lot.batch.receivedAt);

  return (
    <TableRow>
      <TableCell>
        <Tooltip arrow title={lot.id}>
          <Typography
            variant="body2"
            color={textColor}
            fontFamily="monospace"
            sx={{ cursor: 'pointer' }}
          >
            {lotIdShort}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" color={textColor}>
          {formatNumber(lot.quantity)}
        </Typography>
      </TableCell>
      <TableCell>
        <MultiTimezoneTooltip timestamp={receivedAtDateTime}>
          <Typography
            variant="body2"
            color={textColor}
            sx={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}
          >
            {receivedAtDateTime.toLocaleString(DateTime.DATE_MED)}
          </Typography>
        </MultiTimezoneTooltip>
      </TableCell>
      <TableCell>
        <Tooltip arrow title={lot.batch.id}>
          <Typography
            variant="body2"
            color={textColor}
            fontFamily="monospace"
            sx={{ cursor: 'pointer' }}
          >
            {batchIdShort}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          color={lot.batch.shipmentReference ? textColor : 'text.secondary'}
        >
          {lot.batch.shipmentReference ?? 'Not set'}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" color={textColor}>
          {formatNumber(lot.batch.receivedQuantity)}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

function LotDetailsSkeleton() {
  return (
    <Table size="small">
      <LotTableHeader />
      <TableBody>
        {Array.from({ length: 3 }).map((_, index) => (
          <TableRow key={index}>
            <TableCell>
              <Skeleton variant="text" width={60} />
            </TableCell>
            <TableCell align="right">
              <Skeleton variant="text" width={40} />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width={80} />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width={60} />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width={80} />
            </TableCell>
            <TableCell align="right">
              <Skeleton variant="text" width={40} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function InventoryLotDetails(props: InventoryLotDetailsProps) {
  const { itemKey, locationKey, expanded } = props;
  const [showEmptyLots, setShowEmptyLots] = useState(false);

  const { lotsData, isLoading } = useItemLots({
    itemKey,
    locationKey,
    includeEmptyLots: showEmptyLots,
    enabled: expanded,
  });

  if (!expanded) {
    return null;
  }

  const lots = lotsData?.lots ?? [];

  return (
    <TableRow>
      <TableCell colSpan={3} sx={{ padding: 0, borderBottom: 'none' }}>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ bgcolor: 'grey.50', px: 2, py: 1, ml: 6 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Lot breakdown
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showEmptyLots}
                    onChange={(e) => setShowEmptyLots(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption" color="text.secondary">
                    Include empty lots
                  </Typography>
                }
                labelPlacement="start"
                sx={{ mr: 0 }}
              />
            </Box>

            {isLoading ? (
              <LotDetailsSkeleton />
            ) : lots.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No lots found
              </Typography>
            ) : (
              <Table size="small">
                <LotTableHeader />
                <TableBody>
                  {lots.map((lot) => (
                    <LotRow
                      key={lot.id}
                      lot={lot}
                      isZeroQuantity={lot.quantity === 0}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
}
